import { useMessageSocketEventsForMe } from "@@/backoffice/for-providers/messages/use-message-socket-events-for-me";
import { useCheckInSocketEventsForMe } from "@@/shared/statuses/use-check-in-socket-events-for-me";
import { useQueryClient } from "@tanstack/react-query";
import { createContext } from "react";

const GlobalWebSocketEventHandlerContext = createContext(undefined);

/**
 * Provides global WebSocket event handling for the application.
 *
 * This provider listens for check-in events via WebSocket and invalidates
 * the TanStack Query cache for "check-in-statuses" to ensure the latest data is fetched.
 *
 * @param props.children - The React children to render within the provider.
 * @param props.verbose - Optional flag for verbose logging or behavior.
 *
 * @remarks
 * Wrap your application or relevant subtree with this provider to enable
 * automatic cache invalidation on check-in events.
 */
const GlobalWebSocketEventHandlersProvider = (props: {
    children?: React.ReactNode;
    verbose?: boolean;
}) => {
    const queryClient = useQueryClient();

    // When a check-in event happens we invalidate tanstack's
    // cache of check-in-statuses to ensure we have the latest data
    useCheckInSocketEventsForMe({
        onCheckInEvent: () => {
            void queryClient.invalidateQueries({
                // If there are perf issues we can
                // change this to more specific query keys
                queryKey: ["check-in-statuses"],
            });
        },
    });

    // When a message event happens we invalidate tanstack's
    // cache of messages for the order to ensure we have the latest data
    useMessageSocketEventsForMe({
        onMessageEvent: event => {
            void queryClient.invalidateQueries({
                // If there are perf issues we can
                // change this to more specific query keys
                queryKey: ["orders", event.data.references.orderId, "messages"],
            });
        },
        verbose: props.verbose,
    });

    return (
        <GlobalWebSocketEventHandlerContext.Provider value={undefined}>
            {props.children}
        </GlobalWebSocketEventHandlerContext.Provider>
    );
};

export {
    GlobalWebSocketEventHandlerContext,
    GlobalWebSocketEventHandlersProvider,
};
